// ReviewPopup.jsx
import { useState, useEffect } from 'react';
import { Modal, Button, Rate, Input, message } from 'antd';
import { StarOutlined, MessageOutlined } from '@ant-design/icons';
import drEasyApi from '../../Api/Customapi';
import { useLanguage } from '../../context/LanguageContext';

const { TextArea } = Input;

const ReviewPopup = ({ doctor, user, slug }) => {
  const { t, currentLanguage } = useLanguage();
  const [showInitialPopup, setShowInitialPopup] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);

  const getDoctorName = () => {
    try {
      if (!doctor) return t('common.doctor');
      if (typeof doctor.name === 'string' && doctor.name.trim() !== '') {
        return doctor.name;
      } 
      if (doctor.firstName) {
        return `${doctor.firstName} ${doctor.lastName || ''}`;
      }
      return doctor.fullName || t('common.doctor');
    } catch {
      return t('common.doctor');
    }
  };

  useEffect(() => {
    if (doctor && user) {
      const shouldShowPopup = !doctor?.isReviewed && doctor?.canReview;
      if (shouldShowPopup) {
        const timer = setTimeout(() => setShowInitialPopup(true), 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [doctor, user]);

  const handleWantToReview = () => {
    setShowInitialPopup(false);
    setTimeout(()=>{
          setShowReviewModal(true);

    },1200)
  };

  const handleLater = () => setShowInitialPopup(false);

  const handleSubmitReview = async () => {
    setShowInitialPopup(false);
    if (rating === 0) {
      message.warning(t('review.pleaseSelectRating'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        text: reviewText,
        rating,
        centerId: doctor.id || doctor.doctorId,
      };
      await drEasyApi.post(`/api/Review/AddReview`, payload);
      message.success(t('review.submittedSuccessfully'));
      setShowReviewModal(false);
      setRating(0);
      setReviewText('');
    } catch (error) {
      message.error(error.response?.data?.message || t('review.failedToSubmit'));
    } finally {
      setLoading(false);
    }
  };

  const getRatingLabel = (ratingValue) => {
    const labels = {
      1: t('review.ratings.poor'),
      2: t('review.ratings.fair'),
      3: t('review.ratings.good'),
      4: t('review.ratings.veryGood'),
      5: t('review.ratings.excellent')
    };
    return labels[ratingValue] || '';
  };

  if (!doctor) return null;

  const doctorName = String(getDoctorName() || t('common.doctor'));
  const isRTL = currentLanguage === 'ar';

  return (
    <>
      {/* Initial Popup */}
      <Modal
        open={showInitialPopup}
        title={
          <div className={`flex items-center gap-3 text-xl font-semibold `}>
            <StarOutlined className="text-yellow-500" />
            <span>{t('review.titleCenter', { name: doctorName })}</span>
          </div>
        }
        footer={[
          <Button key="later" size="large" onClick={handleLater}>
            {t('review.later')}
          </Button>,
          <Button
            key="review"
            type=""
            size="large"
            onClick={handleWantToReview}
            className="bg-primary text-white hover:bg-secondary rounded-lg px-6 py-2"
          >
            {t('review.yesWantToReview')}
          </Button>,
        ]}
        onCancel={handleLater}
        width={500}
        centered
        direction={isRTL ? 'rtl' : 'ltr'}
      >
        <div className={`py-6 text-lg ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
          <p className="text-gray-700 mb-6">
            {t('review.wouldYouLike', { name: doctorName })}
            {' '}
            {t('review.helpsOthers')}
          </p>
          <div className={`flex items-center gap-2 text-gray-600 `}>
            <MessageOutlined />
            <span>{t('review.onlyTakesMinute')}</span>
          </div>
        </div>
      </Modal>

      {/* Review Modal */}
      <Modal
        open={showReviewModal}
        title={
          <div className={`flex items-center gap-3 text-2xl font-semibold `}>
            <StarOutlined className="text-yellow-500" />
            <span>{t('review.titleCenter', { name: doctorName })}</span>
          </div>
        }
        footer={[
          <Button
            key="cancel"
            size="large"
            onClick={() => setShowReviewModal(false)}
            disabled={loading}
          >
            {t('review.cancel')}
          </Button>,
          <Button
            key="submit"
            type=""
            size="large"
            onClick={handleSubmitReview}
            loading={loading}
            className="bg-goldy text-white  rounded-lg px-8 py-2"
          >
            {t('review.submitReview')}
          </Button>,
        ]}
        onCancel={() => setShowReviewModal(false)}
        width={650}
        centered
        direction={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="py-6 space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Rating Section */}
          <div className="text-center">
            <h3 className="text-xl font-medium text-gray-800 mb-4">
              {t('review.howWouldYouRate')}
            </h3>
            <Rate
              value={rating}
              onChange={setRating}
              style={{ fontSize: '3rem' }}
              className="text-yellow-500"
            />
            {rating > 0 && (
              <div className="mt-3 text-base text-gray-600 font-medium">
                {getRatingLabel(rating)}
              </div>
            )}
          </div>

          {/* Review Text Section */}
          <div>
            <label className={`block text-base font-medium text-gray-800 mb-3 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('review.shareYourExperience')}
            </label>
            <TextArea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder={t('review.tellOthersCenter', { name: doctorName })}
              rows={6}
              maxLength={500}
              showCount
              className={`resize-none text-base p-3 rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>

          {/* Doctor Info */}
          <div className="bg-gray-50 p-5 rounded-xl">
            <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {doctor.profileImage && (
                <img
                  src={doctor.profileImage}
                  alt={doctorName}
                  className="w-16 h-16 rounded-full object-cover shadow"
                />
              )}
             
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ReviewPopup;